import type { FilterRuleEntity } from '@/shared/components/query-builder'
import type {
	FieldViewProps,
	GroupBase,
	OptionType,
	ValueState,
} from '../../types'
import type SelectController from './Controller'

import React, { useMemo, useState } from 'react'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useCreatebleMultiSelectOptions } from '@/shared/components/query-builder/components/CreatebleMultiSelectField'
import { isReferenceRule } from '@/shared/components/query-builder/utils/utils'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Command } from '@/shared/components/ui/command'
import {
	MultiSelect,
	MultiSelectCheckboxItem,
	MultiSelectCheckboxSelectAll,
	MultiSelectEmpty,
	MultiSelectGroup,
	MultiSelectList,
	MultiSelectSearch,
	MultiSelectSeparator,
} from '@/shared/components/ui/multi-select'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'

import PopoverFooter from '../../components/PopoverFooter'
import { gatFlatOptions, isGrouped } from '../../utils'

type SelectViewProps = FieldViewProps<SelectController> & {
	localValue: ValueState<string[]>
}

export default function SelectView({
	field,
	isDirty,
	isRemovable,
	localValue,
	onChange,
	onRemove,
	onApply,
	closePopup,
}: SelectViewProps) {
	const { t } = useTranslation()

	const { operation } = localValue
	const { useSelectOptions } = field
	const [searchText, setSearchText] = useState('')
	const selectOptions = useSelectOptions()
	const options = useMemo(() => selectOptions.data || [], [selectOptions.data])
	const flatOptions = useMemo(() => gatFlatOptions(options), [options])
	const { filteredOptions } = useCreatebleMultiSelectOptions({
		options: flatOptions,
		value: localValue?.value || [],
		searchText,
	})
	const values = localValue?.value || []
	const handleChange = (value: string[]) => onChange({ operation, value })

	return (
		<MultiSelect
			isLoading={selectOptions.isLoading}
			onSearch={() => {}}
			onValueChange={handleChange}
			value={values}
			options={flatOptions}
		>
			<div className='max-w-96 min-w-[320px]'>
				{isReferenceRule(localValue as FilterRuleEntity) && (
					<div className='w-full px-2 pt-2'>
						<Alert variant='warning'>
							<InfoIcon />
							<AlertDescription className='text-xs'>
								{t('general_reference_rule_description', {
									defaultValue:
										'This filed now used as referance to another field.',
								})}
							</AlertDescription>
						</Alert>
					</div>
				)}
				{selectOptions.isLoading ? (
					<div className='flex h-24 items-center justify-center space-x-2'>
						<Spinner />
						<p className='py-2 text-sm font-medium'>
							{t('loading', { defaultValue: 'Loading...' })}
						</p>
					</div>
				) : (
					<Command>
						<MultiSelectSearch
							onValueChange={setSearchText}
							value={searchText}
							placeholder={
								field.placeholder ??
								t('general_search', { defaultValue: 'Search' })
							}
						/>
						{isGrouped(options) ? (
							<Virtualized asChild>
								<MultiSelectList className='max-h-[400px] scroll-pt-12'>
									<MultiSelectEmpty>
										{t('general_no_results_found', {
											defaultValue: 'No results found',
										})}
									</MultiSelectEmpty>
									{!!filteredOptions.length && (
										<MultiSelectGroup className='sticky top-0 z-10 bg-popover'>
											<MultiSelectCheckboxSelectAll
												values={filteredOptions.map(({ value }) => value)}
											>
												{t('general_select_all', {
													defaultValue: 'Select all',
												})}
											</MultiSelectCheckboxSelectAll>
										</MultiSelectGroup>
									)}
									<MultiSelectSeparator className='sticky top-10 z-10' />
									<VirtualizedVirtualizer>
										{(options as GroupBase<OptionType>[]).map((optionGroup) => (
											<MultiSelectGroup
												key={optionGroup.label}
												heading={optionGroup.label}
											>
												{optionGroup.options?.map((option) => (
													<MultiSelectCheckboxItem
														key={option.value}
														value={option.value}
														keywords={[option.label]}
													>
														{field.formatOptionLabel?.(option) || option.label}
													</MultiSelectCheckboxItem>
												))}
											</MultiSelectGroup>
										))}
									</VirtualizedVirtualizer>
								</MultiSelectList>
							</Virtualized>
						) : (
							<Virtualized asChild>
								<MultiSelectList className='max-h-[400px] scroll-pt-12'>
									<MultiSelectEmpty>
										{t('general_no_results_found', {
											defaultValue: 'No results found',
										})}
									</MultiSelectEmpty>
									{!!filteredOptions.length && (
										<MultiSelectGroup className='sticky top-0 z-10 bg-popover'>
											<MultiSelectCheckboxSelectAll
												values={filteredOptions.map(({ value }) => value)}
											>
												{t('general_select_all', {
													defaultValue: 'Select all',
												})}
											</MultiSelectCheckboxSelectAll>
										</MultiSelectGroup>
									)}
									<MultiSelectSeparator className='sticky top-10 z-10' />
									<MultiSelectGroup>
										<VirtualizedVirtualizer>
											{filteredOptions.map((option) => (
												<MultiSelectCheckboxItem
													key={option.value}
													keywords={[option.label]}
													value={option.value}
												>
													{field.formatOptionLabel?.(option) || option.label}
												</MultiSelectCheckboxItem>
											))}
										</VirtualizedVirtualizer>
									</MultiSelectGroup>
								</MultiSelectList>
							</Virtualized>
						)}
					</Command>
				)}
				<PopoverFooter
					isDisabledApply={!isDirty}
					onRemove={isRemovable ? onRemove : undefined}
					onApply={() => {
						onApply()
						closePopup()
					}}
				/>
			</div>
		</MultiSelect>
	)
}
